import { DocumentFilters, DocumentFilterKey } from 'interfaces';

const VALID_STATUS_KEYS: Set<DocumentFilterKey> = new Set([
  'new',
  'awaiting_signing',
  'awaiting_my_signing',
  'rejected_signing',
  'signed',
  'awaiting_approval',
  'awaiting_my_approval',
  'rejected_approval',
  'approved',
  'awaiting_agreement',
  'awaiting_my_agreement',
  'agreed',
  'rejected_agreement',
  'agreement_declined',
  'signing_declined',
]);

export const parseFiltersFromUrl = (searchParams: URLSearchParams): DocumentFilters => {
  const nameParam = searchParams.get('name');
  const statusParam = searchParams.get('status');
  const dateFromParam = searchParams.get('dateFrom');
  const dateToParam = searchParams.get('dateTo');

  const status: DocumentFilterKey[] = statusParam
    ? (statusParam.split(',').filter((s): s is DocumentFilterKey => VALID_STATUS_KEYS.has(s as DocumentFilterKey)))
    : [];

  return {
    name: nameParam?.trim() || null,
    status,
    dateFrom: dateFromParam && dateFromParam !== 'null' ? dateFromParam : null,
    dateTo: dateToParam && dateToParam !== 'null' ? dateToParam : null,
  };
};

export const serializeFiltersToUrl = (
  searchParams: URLSearchParams,
  filters: Partial<DocumentFilters>
): URLSearchParams => {
  const newParams = new URLSearchParams(searchParams);

  if (filters.name !== undefined) {
    if (filters.name && filters.name.trim()) {
      newParams.set('name', filters.name.trim());
    } else {
      newParams.delete('name');
    }
  }

  if (filters.status !== undefined) {
    if (filters.status.length > 0) {
      newParams.set('status', filters.status.join(','));
    } else {
      newParams.delete('status');
    }
  }

  if (filters.dateFrom !== undefined) {
    if (filters.dateFrom) {
      newParams.set('dateFrom', filters.dateFrom);
    } else {
      newParams.delete('dateFrom');
    }
  }

  if (filters.dateTo !== undefined) {
    if (filters.dateTo) {
      newParams.set('dateTo', filters.dateTo);
    } else {
      newParams.delete('dateTo');
    }
  }

  return newParams;
};

export const areFiltersEqual = (
  a?: DocumentFilters | null,
  b?: DocumentFilters | null
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;

  const aName = a.name?.trim() || null;
  const bName = b.name?.trim() || null;
  if (aName !== bName) {
    return false;
  }

  if (a.dateFrom !== b.dateFrom || a.dateTo !== b.dateTo) {
    return false;
  }

  const aStatus = a.status || [];
  const bStatus = b.status || [];
  if (aStatus.length !== bStatus.length) {
    return false;
  }

  const sortedA = [...aStatus].sort();
  const sortedB = [...bStatus].sort();
  return sortedA.every((val, index) => val === sortedB[index]);
};

export const buildDocumentViewUrl = (
  pathname: string,
  searchParams: URLSearchParams,
  modalSection: string,
  documentId: string
): string => {
  const newParams = new URLSearchParams(searchParams);
  newParams.set('modalSection', modalSection);
  newParams.set('document_id', documentId);
  newParams.delete('webviewer_file_id');
  const query = newParams.toString();
  return query ? `${pathname}?${query}` : pathname;
};

export const cleanDocumentViewUrl = (
  pathname: string,
  searchParams: URLSearchParams
): string => {
  const newParams = new URLSearchParams(searchParams);
  newParams.delete('modalSection');
  newParams.delete('document_id');
  newParams.delete('webviewer_file_id');
  const query = newParams.toString();
  return query ? `${pathname}?${query}` : pathname;
};
